import { useMemo, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';

import KOLCoverImagesPresenter from '../../app/kols/presenters/KOLCoverImagesPresenter';
import KOLDetailPresenter from '../../app/kols/presenters/KOLDetailPresenter';
import KOLUserDetailPresenter from '../../app/kols/presenters/KOLUserDetailPresenter';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/Button';
import LoadingModal from '../../components/modal/LoadingModal';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import { useDeletePintoEbookCollectionMutation } from '../../core/graphql/operation-hooks';
import useQueryPintoEbookCollection from '../../hooks/useQueryPintoEbookCollection';

function KOLDetailPage() {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const kolId = searchParams.get('id');
  const history = useHistory();

  const { fetchPintoEbookCollection, pintoEbookCollection, isLoadingPintoEbookCollection } = useQueryPintoEbookCollection();
  const [deletePintoEbookCollection, { loading: isDeleting }] = useDeletePintoEbookCollectionMutation();

  useEffect(() => {
    if (kolId) {
      fetchPintoEbookCollection(kolId);
    }
  }, [kolId, fetchPintoEbookCollection]);

  const onDelete = async () => {
    if (!kolId) return;
    if (!window.confirm('Are you sure you want to delete this KOL?')) return;
    try {
      const res = await deletePintoEbookCollection({ variables: { ebookCollectionId: kolId } });
      if (res.data?.deletePintoEbookCollection?.success) {
        ToastComponent({ label: 'Delete successful!', type: IToastType.SUCCESS });
        history.push('/kols');
      } else {
        ToastComponent({ label: res.data?.deletePintoEbookCollection?.message || 'Delete failed.', type: IToastType.ERROR });
      }
    } catch (err) {
      ToastComponent({ label: 'An error occurred.', type: IToastType.ERROR });
    }
  };

  if (!kolId) {
    return <div className="flex items-center justify-center w-full h-[60vh] text-colorsBrandWarmBlack02LabelSecondary">No KOL ID provided</div>;
  }

  return (
    <div className="space-y-12">
      {isLoadingPintoEbookCollection && !pintoEbookCollection ? (
        <LoadingModal />
      ) : (
        <>
          <div className="sticky top-0 z-50 px-5 bg-componentsBgGrouped02">
            {/* section: breadcrumbs */}
            <Breadcrumbs items={[{ name: 'KOLs', url: '/kols' }, { name: pintoEbookCollection?.title || 'KOL Detail' }]} />

            {/* section: title */}
            <div className="flex flex-col justify-between pt-5 md:flex-row">
              <div className="w-[70%] h-full">
                <h1 className="font-dbh text-[28px] break-words break-word">{pintoEbookCollection?.title || 'KOL Detail'}</h1>
              </div>

              <div className="flex items-center space-x-12">
                <Link to="/kols">
                  <Button type="outline" color="gray" className="space-x-4">
                    Back
                  </Button>
                </Link>

                {pintoEbookCollection && (
                  <>
                    <Link to={`/kols/edit?id=${pintoEbookCollection._id}`}>
                      <Button type="outline" color="black" className="space-x-4">
                        <div>Edit</div>
                      </Button>
                    </Link>
                    <Button type="outline" color="red" className="space-x-4" disabled={isDeleting} onClick={onDelete}>
                      <div>Delete</div>
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* section: main */}
            <div className="mb-0 divider opacity-10"></div>
          </div>

          {/* Collection Details */}
          {pintoEbookCollection && <KOLDetailPresenter collectionData={pintoEbookCollection} />}

          {/* Cover Images */}
          {pintoEbookCollection && <KOLCoverImagesPresenter collectionData={pintoEbookCollection} />}

          {/* KOL User Information */}
          {pintoEbookCollection && <KOLUserDetailPresenter collectionData={pintoEbookCollection} />}
        </>
      )}
    </div>
  );
}

export default KOLDetailPage;
